import { useState } from 'react';
import useNswagClient from '../../hooks/api/useNswagClient';
import { useConfigurations } from '../../components/shared/useConfigurations';
import { ConfigurationName } from '../../enums/ConfigurationName';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

type OperationalRange = {
    start: Date;
    end: Date;
    isShiftEnabled: boolean;
};

const normalizeTimezone = (timezoneValue: string): string => {
  if (!timezoneValue) return 'UTC';

  const timezone = timezoneValue.trim();

  if (timezone.includes('/') && !timezone.startsWith('UTC')) {
    return timezone;
  }

  const offsetRegex = /^(?:utc)?([+-])(\d{1,2})(?::?(\d{2}))?$/i;
  const match = timezone.match(offsetRegex);

  if (match) {
    const sign = match[1];
    const hours = match[2].padStart(2, '0');
    const minutes = match[3] || '00';

    return `${sign}${hours}:${minutes}`;
  }

  return 'UTC';
};

const useShiftConfiguration = () => {
  const { getStoreTimezone } = useNswagClient();
  const { loadConfigurationsForStore } = useConfigurations();

  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [startOperationalDay, setStartOperationalDay] = useState<Date | null>(null);
  const [endOperationalDay, setEndOperationalDay] = useState<Date | null>(null);
  const [isShiftEnabled, setIsShiftEnabled] = useState(false);
  const [storeTimeZone, setStoreTimeZone] = useState<string>('UTC');

  const loadOperationalDay = async (storeNumber: string, franchiseName: string) => {
    const [timezoneResponse, configurations] = await Promise.all([
      getStoreTimezone(storeNumber, franchiseName),
      loadConfigurationsForStore(storeNumber),
    ]);
    const rawTimeZone = timezoneResponse?.timeZoneId ?? 'UTC';
    const timeZone = normalizeTimezone(rawTimeZone);
    setStoreTimeZone(timeZone);

    // Use utcOffset for offset formats, tz for IANA identifiers
    const now = timeZone.match(/^[+-]\d{2}:\d{2}$/)
      ? dayjs().utcOffset(timeZone)
      : dayjs().tz(timeZone);

    setCurrentDate(now.toDate());

    const defaultStart = now.startOf('day');
    const defaultEnd = now.endOf('day');
    const enabled = configurations[ConfigurationName[ConfigurationName.ShiftConfigurationEnabled]] === 'true';
    setIsShiftEnabled(enabled);

    if (!enabled) {
      setStartOperationalDay(defaultStart.toDate());
      setEndOperationalDay(defaultEnd.toDate());
      return { start: defaultStart, end: defaultEnd };
    }

    const shiftStartString = configurations[ConfigurationName[ConfigurationName.ShiftStartTime]];

    if (!shiftStartString || shiftStartString === '00:00:00') {
      setStartOperationalDay(defaultStart.toDate());
      setEndOperationalDay(defaultEnd.toDate());
      return { start: defaultStart, end: defaultEnd };
    }

    const [hours, minutes, seconds] = shiftStartString.split(':').map(Number);
    const shiftStartToday = timeZone.match(/^[+-]\d{2}:\d{2}$/)
      ? dayjs().utcOffset(timeZone).startOf('day')
      : dayjs().tz(timeZone).startOf('day')
        .add(hours, 'hour')
        .add(minutes, 'minute')
        .add(seconds, 'second');

    const adjustedStart = now.isBefore(shiftStartToday)
      ? shiftStartToday.subtract(1, 'day')
      : shiftStartToday;

    const adjustedEnd = adjustedStart.add(1, 'day').subtract(1, 'second');

    setStartOperationalDay(adjustedStart.toDate());
    setEndOperationalDay(adjustedEnd.toDate());

    return { start: adjustedStart, end: adjustedEnd };
  };


  const calculateOperationalTransactionsRange = (
    startDate: Date,
    endDate: Date,
    storeNumber: string,
    _franchiseName: string,
    initialLoad: boolean,
  ): Promise<OperationalRange> => {
    return loadConfigurationsForStore(storeNumber).then(configurations => {
      const isEnabled = configurations[ConfigurationName[ConfigurationName.ShiftConfigurationEnabled]] === 'true';

      if (!isEnabled) {
        return {
          start: new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate()),
          end: new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate(), 0, 0, -1),
          isShiftEnabled: false,
        };
      }

      const shiftStartString = configurations[ConfigurationName[ConfigurationName.ShiftStartTime]];

      if (!shiftStartString) {
        const start = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());

        let end: Date;

        if (initialLoad) {
          end = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate(), 0, 0, -1);
        } else {
          end = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
        }

        return {
          start,
          end,
          isShiftEnabled: true,
        };
      }

      const [hours, minutes, seconds] = shiftStartString.split(':').map(Number);

      const start = new Date(startDate);
      start.setHours(hours, minutes, seconds, 0);

      const end = new Date(endDate);
      if (initialLoad) {
        end.setDate(end.getDate() - 1);
      }
      end.setHours(hours, minutes, seconds, 0);
      end.setDate(end.getDate() + 1);
      end.setSeconds(end.getSeconds() - 1);

      return {
        start,
        end,
        isShiftEnabled: true,
      };
    });
  };

  const calculateOperationalReportsRange = (
    startDate: Date,
    endDate: Date,
    storeNumber: string,
    _franchiseName: string,
  ): Promise<OperationalRange> => {
    return loadConfigurationsForStore(storeNumber).then(configurations => {
      const isEnabled = configurations[ConfigurationName[ConfigurationName.ShiftConfigurationEnabled]] === 'true';

      if (!isEnabled) {
        return {
          start: new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate()),
          end: new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate()),
          isShiftEnabled: false,
        };
      }

      const shiftStartString = configurations[ConfigurationName[ConfigurationName.ShiftStartTime]];

      if (!shiftStartString) {
        return {
          start: new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate()),
          end: new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate()),
          isShiftEnabled: true,
        };
      }

      const [hours, minutes, seconds] = shiftStartString.split(':').map(Number);

      const start = new Date(startDate);
      start.setHours(hours, minutes, seconds, 0);

      const end = new Date(endDate);
      end.setHours(hours, minutes, seconds, 0);
      end.setDate(end.getDate() + 1);
      end.setSeconds(end.getSeconds() - 1);

      return {
        start,
        end,
        isShiftEnabled: true,
      };
    });
  };

  return {
    currentDate,
    startOperationalDay,
    endOperationalDay,
    isShiftEnabled,
    storeTimeZone,
    loadOperationalDay,
    calculateOperationalTransactionsRange,
    calculateOperationalReportsRange,
  };
};

export default useShiftConfiguration;
